import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { JoyrideGuidedTour } from "components/SimpleComponents/GuidedTour/GuidedTours";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { getIsCypressRunning } from "constants/constants";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useNavigate } from "react-router";
import { useMount } from "react-use";
import { ONE_WEEK_OF_MS } from "utils/timeUtils";
import { PageNameEnum } from "types/enums";
import { TrendChartCard } from "./TrendChartCard";
import { PnlChartCard } from "./PnlChartCard";
import { MachineBreakdownChartCard } from "./MachineBreakdownChartCard";
import { TimelineChartCard } from "./TimelineChartCard";
import RenderIfVisible from "react-render-if-visible";

const TimelinePageSection = () => {
  const {
    isTourEnabled,
    isPnlBreakdownChartVisible: isPnlBreakdownChartVisibleSetting,
    isTrendChartVisible: isTrendChartVisibleSetting,
  } = useSettings();
  const isPnlBreakdownChartVisible =
    isPnlBreakdownChartVisibleSetting &&
    !window.location.search.includes("hidePnl=true");
  const isTrendChartVisible =
    isTrendChartVisibleSetting &&
    !window.location.search.includes("hideTrend=true");
  const [
    {
      general: { enableMachineBreakdownChart },
    },
  ] = useFlightConfig();
  const isStorybook = useIsStorybook();
  const isCypressRunning = getIsCypressRunning();
  useQuickViewQueryParams();
  return (
    <>
      {isTrendChartVisible && (
        <RenderIfVisible
          initialVisible={isCypressRunning}
          defaultHeight={480}
          stayRendered={isCypressRunning}
        >
          <TrendChartCard />
        </RenderIfVisible>
      )}
      {isPnlBreakdownChartVisible && (
        <RenderIfVisible
          initialVisible={isCypressRunning}
          defaultHeight={550}
          stayRendered={isCypressRunning}
        >
          <PnlChartCard />
        </RenderIfVisible>
      )}
      {isTourEnabled && <JoyrideGuidedTour />}
      {(enableMachineBreakdownChart || isStorybook || isCypressRunning) && (
        <RenderIfVisible
          initialVisible={isCypressRunning}
          defaultHeight={540}
          stayRendered={isCypressRunning}
        >
          <MachineBreakdownChartCard />
        </RenderIfVisible>
      )}
      {window.location.search.includes("hideTimeline=true") ? null : (
        <RenderIfVisible
          initialVisible={isCypressRunning}
          defaultHeight={550}
          stayRendered={isCypressRunning}
        >
          <TimelineChartCard />
        </RenderIfVisible>
      )}
    </>
  );
};
export default TimelinePageSection;

/** if url query params include "quickView=lastWeek",
 * 1. collapse unused charts (only show timeline)
 * 2. set focus range url params to the last full week from Sunday-Sunday 00:00-00:00 */
function useQuickViewQueryParams() {
  const navigate = useNavigate();
  const search = window.location.search;
  const { translate } = useFlightTranslation();
  const isGroupSummaryExpandedLSKey = `collapsibles:${
    PageNameEnum.timeline
  }:${translate("Group_Summary")}`;
  const isTrendChartExpandedLSKey = `collapsibles:${
    PageNameEnum.timeline
  }:${translate("Group_Trend")}`;
  const isPnlChartExpandedLSKey = `collapsibles:${
    PageNameEnum.timeline
  }:${translate("Performance_&_Loss_Breakdown")}`;
  useMount(() => {
    // URLSearchParams not working immediately after reload, so use search string directly instead
    if (!search.includes("quickView=")) {
      return;
    } else if (
      search.includes("quickView=lastWeek") &&
      !search.includes("ge=")
    ) {
      // collapse unnecessary charts
      // (then, we'll need to reload since these are local storage items, and to use the new focusRange query params)
      window.localStorage.setItem(isGroupSummaryExpandedLSKey, "false");
      window.localStorage.setItem(isTrendChartExpandedLSKey, "false");
      window.localStorage.setItem(isPnlChartExpandedLSKey, "false");

      // then, if quickView=lastWeek, set focus range to last week
      const now = new Date();
      const lastSunday =
        now.getDay() === 0
          ? now
          : new Date(now.setDate(now.getDate() - now.getDay()));
      const lastSundayMidnight = new Date(
        lastSunday.setHours(0, 0, 0, 0)
      ).getTime();
      navigate({
        search: `${window.location.search}&ge=${
          lastSundayMidnight - ONE_WEEK_OF_MS
        }&lt=${lastSundayMidnight}`,
      });

      window.location.reload();
    }
  });
}
